/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  TextField,
  Switch,
  Paper,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons';
import * as lib from '~/dependencies';
import * as NuacomComponent from '~/components';
import * as actions from '~/store/actions';
import messagesToast from '~/validations/messagesNotifications';
import { useNewContact, useContactInfo, useUpdateContact } from '~/hooks';
import { eventTracker } from '~/helpers';
import { Select } from '~/components/UI';
import {
  Container,
  BoxInput,
  Content,
  TopButtons,
  BoxSwitch,
  Dialog,
  DialogContent,
  DialogTitle,
  ButtonStyled,
  PaperContent,
} from './styles';

export default function NewContacts(props) {
  const { location, history, match } = props;

  const [personalContacts, setPersonalContacts] = useState(true);
  const [contactEdit, setContactEdit] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [landline, setLandline] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState(null);
  const [groups, setGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [orgOrGroupValue, setOrgOrGroupValue] = useState('');
  const [dialogType, setDialogType] = useState('');
  const [loading, setLoading] = useState(false);

  const firstNameInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  // HOOK's
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();
  const [
    { newContact, errorNewContact, setErrorNewContact },
    setNewContact,
  ] = useNewContact();
  const [{ updateContact, errorUpdate }, setUpdateContact] = useUpdateContact();
  const [contactInfo, setContactInfo] = useContactInfo(null);

  const organizationList = useSelector(state => state?.contact?.organizations);
  const groupList = useSelector(state => state?.contact?.groups);

  const handleTextFieldFocus = useCallback(
    responseErrorMessage => {
      if (
        responseErrorMessage?.includes(
          'First name or Last name cannot be empty.'
        )
      ) {
        if (firstNameInputRef?.current) {
          firstNameInputRef.current?.focus();
          setErrorNewContact(null);
        }
      } else if (
        responseErrorMessage?.includes(
          'Mobile or landline or email cannot be empty.'
        )
      ) {
        if (mobileInputRef?.current) {
          mobileInputRef.current?.focus();
          setErrorNewContact(null);
        }
      }
    },
    [firstNameInputRef, mobileInputRef, setErrorNewContact]
  );

  useEffect(() => {
    if (location.state) {
      if (location.state.edit && location.state.id) {
        setContactEdit(true);
        setContactInfo(location.state.id);
      }
      if (location.state.landLine !== null) {
        setMobile(location.state.landLine || '');
      }
    }
  }, [location.state, setContactEdit, setContactInfo]);

  useEffect(() => {
    const { tab } = location.state;
    if (contactInfo !== null || undefined) {
      setFirstName(contactInfo.first_name);
      setLastName(contactInfo.last_name);
      setMobile(contactInfo.mobile);
      setLandline(contactInfo.landline);
      setEmail(contactInfo.email);
      setGroups(contactInfo.groups || []);

      if (
        Array.isArray(contactInfo.organization) &&
        contactInfo.organization?.length
      ) {
        setOrganization(contactInfo.organization[0]);
      } else {
        setOrganization({ name: '' });
      }
    }
    setPersonalContacts(tab === 0);
  }, [contactInfo, location.state]);

  useEffect(() => {
    if (newContact) {
      // dispatch(actions.checkNewContacts('All', true));
      // dispatch(actions.checkNewContacts('Personal', true));
      lib.ToastMessage('success', messagesToast().CONTACTS.new.success);
      if (location?.state?.fromCallDetails) {
        dispatch(actions.resetCalls());
        dispatch(actions.setNoMoreItems(false));
        dispatch(actions.fetchCalllogs());
        history.goBack();
      } else {
        history.push({
          pathname: `${match.path}/contacts`,
          state: { tab: location?.state?.tab || 0 },
        });
      }
    }
  }, [
    newContact,
    history,
    match,
    dispatch,
    personalContacts,
    location,
    errorUpdate,
  ]);

  useEffect(() => {
    if (updateContact) {
      lib.ToastMessage('success', messagesToast().CONTACTS.edit.success);
      dispatch(actions.resetCalls());
      dispatch(actions.setNoMoreItems(false));
      dispatch(actions.fetchCalllogs());
    }
  }, [dispatch, personalContacts, updateContact]);

  useEffect(() => {
    if (errorUpdate) {
      lib.ToastMessage('error', errorUpdate);
      handleTextFieldFocus(errorUpdate);
    }
  }, [errorUpdate, handleTextFieldFocus]);

  useEffect(() => {
    if (errorNewContact) {
      lib.ToastMessage('error', errorNewContact);
      handleTextFieldFocus(errorNewContact);
      setErrorNewContact(undefined);
    }
  }, [errorNewContact, handleTextFieldFocus, setErrorNewContact]);

  function dataContact(id = null) {
    const { tab } = location.state;
    const dataValues = {
      first_name: firstName,
      last_name: lastName,
      mobile,
      landline,
      email,
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      birthday: '',
      comment: '',
    };

    if (tab !== personalContacts) {
      dataValues.changedTab = true;
    }

    if (organization?.id) {
      dataValues.organization = organization.id.toString();
    } else {
      dataValues.organization = null;
    }

    if (groups?.length > 0) {
      dataValues.groups = groups.map(({ group_id }) => group_id);
    } else {
      dataValues.groups = null;
    }

    dataValues.personal = !!personalContacts;

    if (id) {
      dataValues.id = id;
    }

    return dataValues;
  }

  function submit() {
    const contacts = dataContact();
    setNewContact(contacts);
    eventTracker(events => events.AddAContact);
  }

  function edit() {
    setUpdateContact(location.state.id, dataContact(location.state.id));
    setTimeout(() => {
      handleClose();
    }, 1200);
  }

  function addNumber(number, type) {
    const verifyNumber = number
      ? String(number).replace(/[^0-9*#+]+/, '')
      : number;

    if (type === 'mobile') {
      setMobile(verifyNumber);
    } else {
      setLandline(verifyNumber);
    }
  }

  function handleOrganizationChange(event, value) {
    setOrganization(value);
  }

  function handleGroupsChange(event, value) {
    setGroups(value);
  }

  function handleClose() {
    if (location.state.prevScreen === 'TerminatedCall') {
      history.push('/app/dialPad');
    } else {
      history.goBack();
    }
  }

  function handleNewOrgOrGroups(type) {
    if (type === 'org') {
      setDialogType('org');
      setDialogTitle(`${t('screens.newContacts.addOrg')}`);
    } else {
      setDialogType('group');
      setDialogTitle(`${t('screens.newContacts.addGroup')}`);
    }
    setOpenDialog(true);
  }

  function handleDialogClose() {
    setDialogType('');
    setDialogTitle('');
    setOpenDialog(false);
  }

  async function handleCreateOrgOrGroups() {
    try {
      if (orgOrGroupValue) {
        setLoading(true);
        if (dialogType === 'org') {
          const newOrg = await dispatch(
            actions.createOrganization({ name: orgOrGroupValue })
          );
          setOrganization({ ...newOrg, name: orgOrGroupValue });
          lib.ToastMessage('success', messagesToast().CONTACTS.newOrg.success);
        } else {
          const newGroup = await dispatch(
            actions.createGroups({ name: orgOrGroupValue })
          );
          setGroups([...groups, { ...newGroup, group_name: orgOrGroupValue }]);
          lib.ToastMessage('success', messagesToast().CONTACTS.group.success);
        }
      }
    } catch (err) {
      lib.ToastMessage('error', messagesToast().CONTACTS.newOrg.error);
    }

    setLoading(false);
    setOpenDialog(false);
    setOrgOrGroupValue('');
    setDialogTitle('');
  }

  return (
    <>
      <NuacomComponent.Header {...props} name>
        <TopButtons>
          {!newContact && !updateContact && (
            <>
              {contactEdit ? (
                <Button
                  fullWidth
                  className="active"
                  data-test-id="edit-new-contact-btn"
                  onClick={() => edit()}>
                  {t('screens.newContacts.save')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  className="active"
                  data-test-id="save-new-contact-btn"
                  onClick={() => submit()}>
                  {t('screens.newContacts.save')}
                </Button>
              )}
            </>
          )}
          <Button
            fullWidth
            data-test-id="cancel-new-contact-btn"
            onClick={handleClose}>
            {t('screens.newContacts.cancel')}
          </Button>
        </TopButtons>
      </NuacomComponent.Header>
      <Container>
        <span className="titlePage">
          {contactEdit
            ? t('screens.newContacts.editContact')
            : t('screens.newContacts.newContact')}
        </span>

        <Content>
          <BoxInput>
            <TextField
              required
              autoFocus
              inputRef={firstNameInputRef}
              disabled={newContact || updateContact}
              className="inputField"
              label={t('screens.newContacts.firstName')}
              value={`${firstName}`}
              onChange={e => setFirstName(e.target.value)}
              data-test-id="first-name"
            />
          </BoxInput>
          <BoxInput>
            <TextField
              label={t('screens.newContacts.lastName')}
              disabled={newContact || updateContact}
              className="inputField"
              value={`${lastName}`}
              onChange={e => setLastName(e.target.value)}
              data-test-id="last-name"
            />
          </BoxInput>
          <BoxInput>
            <TextField
              label={t('screens.newContacts.mobile')}
              required
              inputRef={mobileInputRef}
              disabled={newContact || updateContact}
              className="inputField"
              value={`${mobile}`}
              onChange={e => addNumber(e.target.value, 'mobile')}
              data-test-id="mobile"
            />
          </BoxInput>
          <BoxInput>
            <TextField
              required
              label={t('screens.newContacts.landline')}
              disabled={newContact || updateContact}
              className="inputField"
              value={`${landline}`}
              onChange={e => addNumber(e.target.value, 'landline')}
              data-test-id="land-line"
            />
          </BoxInput>
          <BoxInput>
            <TextField
              label={t('screens.newContacts.email')}
              disabled={newContact || updateContact}
              className="inputField"
              value={`${email}`}
              onChange={e => setEmail(e.target.value)}
              data-test-id="e-email"
            />
          </BoxInput>
          <Select
            title={t('screens.newContacts.organization')}
            label={t('screens.newContacts.organization')}
            multiple={false}
            disabled={newContact || updateContact}
            value={organization}
            valueList={organizationList}
            handleChange={handleOrganizationChange}
            PaperComponent={({ children }) => (
              <Paper elevation={1}>
                {children}
                <PaperContent
                  onMouseDown={ev => {
                    ev.preventDefault();
                    handleNewOrgOrGroups('org');
                  }}
                  role="button"
                  tabIndex={0}>
                  <AddIcon fontSize="small" />
                  <span style={{ fontSize: 14 }}>
                    {t('screens.newContacts.addOrg')}
                  </span>
                </PaperContent>
              </Paper>
            )}
            selectedOption={(option, value) => option.id === value.id}
            labelOption={option => option.name}
          />
          <Select
            title={t('screens.newContacts.groups')}
            label={t('screens.newContacts.groups')}
            multiple
            disabled={newContact || updateContact}
            values={groups}
            valueList={groupList}
            handleChange={handleGroupsChange}
            PaperComponent={({ children }) => (
              <Paper elevation={1}>
                {children}
                <PaperContent
                  onMouseDown={ev => {
                    ev.preventDefault();
                    handleNewOrgOrGroups('group');
                  }}
                  role="button"
                  tabIndex={0}>
                  <AddIcon fontSize="small" />
                  <span style={{ fontSize: 14 }}>
                    {t('screens.newContacts.addGroup')}
                  </span>
                </PaperContent>
              </Paper>
            )}
            selectedOption={(option, value) =>
              option.group_id === value.group_id
            }
            labelOption={option => option.group_name}
          />
          <BoxSwitch>
            <span>{t('screens.newContacts.shared')}</span>
            <Switch
              checked={personalContacts}
              onChange={() => setPersonalContacts(!personalContacts)}
              value={personalContacts}
              color="primary"
            />
            <span>{t('screens.newContacts.private')}</span>
          </BoxSwitch>
        </Content>
      </Container>
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="customized-dialog-title">
        <DialogTitle id="customized-dialog-title" disableTypography>
          <IconButton
            aria-label="close"
            style={{ padding: 3, position: 'absolute', left: 20 }}
            onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
          <span>{dialogTitle}</span>
        </DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            placeholder="Name*"
            autoFocus
            value={orgOrGroupValue}
            onChange={event => {
              setOrgOrGroupValue(event.target.value);
            }}
          />
          <ButtonStyled
            style={{
              alignSelf: 'flex-end',
              marginTop: 20,
            }}
            onClick={handleCreateOrgOrGroups}>
            {loading ? (
              <CircularProgress
                variant="indeterminate"
                disableShrink
                className="loader"
                size={15}
                thickness={4}
              />
            ) : (
              <span>{t('screens.newContacts.btnCreate')}</span>
            )}
          </ButtonStyled>
        </DialogContent>
      </Dialog>
    </>
  );
}

NewContacts.defaultProps = {
  history: [],
  match: [],
  location: [],
};

NewContacts.propTypes = {
  history: lib.PropTypes.instanceOf(Object),
  match: lib.PropTypes.instanceOf(Object),
  location: lib.PropTypes.instanceOf(Object),
};
